import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useHistory} from 'react-router-dom';
import mime from 'mime-types';
import AbstractDropdownView from 'common/components/AbstractDropdownView';
import {InteractionsMenuItem} from '../InteractionsMenuItems';
import ItemEditMetadata from '../ItemEditMetadata';
import ItemEditFile from '../ItemEditFile';
import ConfirmDelete from '../ConfirmDelete';
import AnimatedUploadingFilePopUp from '../../AnimatedUploadingFilePopUp';
import interaction from 'common/assets/more_414141.svg';
import readMore from '../../../../modules/Inspection/assets/read_more-4A4A4A.svg';
import viewIcon from '../../../../modules/Inspection/assets/search-444444.svg';
import deleteFileIcon from '../../../../modules/Inspection/assets/delete-4A4A4A.svg';
import downloadFileIcon from '../../../../modules/Inspection/assets/download-4A4A4A.svg';
import {pathnames as P} from 'common/constants/pathnames';
import {errors as E} from 'common/constants/errors';
import {fileTypeId as T} from '../../../../modules/Inspection/constants/fileTypeId';
import {downloadFile} from '../../../utils/files/downloadFile';
import styles from './styles.module.scss';

const InteractionsMenu = ({
                            fileTypeID,
                            orderFileTypeID,
                            mimeType,
                            fileID,
                            stepID,
                            content,
                            fileName,
                            fileExtension,
                            onFileDelete,
                            deleteFile,
                            deleteOrderFile,
                            uploadFile,
                            uploadOrderFile,
                            updateFile,
                            updateFileMetadata,
                            getFileList,
                            getFile,
                            getFileFromOrder,
                            editFormValues,
                            shumaId,
                            orderID,
                            getShumaStepStatus,
                            isFileFromOrder,
                            showGlobalErrorMessage
                          }) => {
  const [clickOutsideAllowed, setClickOutsideAllowed] = useState(true);
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const handleFileDownload = () => {
    Promise.resolve()
      .then(() => setLoading(true))
      .then(async () => {
        if (!Boolean(content)) {
          return await updateFile();
        }
      })
      .then((updatedContent) => {
        const downloadLink = updatedContent || content;
        if (Boolean(downloadLink)) {
          downloadFile(downloadLink, fileName);
        }
      })
      .catch(err => showGlobalErrorMessage(err.message))
      .finally(() => setTimeout(() => {
        setLoading(false)
      }, 1000));
  };

  const handleDeleteFile = async () => {
    const errorCode = isFileFromOrder
      ? await deleteOrderFile({orderID, fileID})
      : await deleteFile({shumaId, fileID});
    if (errorCode < 0) {
      showGlobalErrorMessage(E.SERVER_ERROR);
    } else {
      await onFileDelete();
      await getShumaStepStatus({shumaId});
    }
  };

  const getType = () => {
    switch (mimeType) {
      case 'application/pdf':
        return 'pdf';
      case 'image/gif':
      case 'image/jpeg':
      case 'image/jpg':
      case 'image/tiff':
      case 'image/png':
        return 'image';
      default:
        return null;
    }
  };

  const updateFileOpenInTab = async () => {
    try {
      const body = isFileFromOrder
        ? await getFileFromOrder({orderID, fileID})
        : await getFile({shumaId, fileID});
      const mimeType = mime.lookup(fileExtension);
      return `data:${mimeType};base64,${body}`;
    } catch (e) {
      console.error(e)
    }
  };

  const handleFileOpenInTab = () => {
    if (getType()) {
      Promise.resolve()
        .then(updateFileOpenInTab)
        .then((content) => {
          if (content) {
            let file = getType() === 'image'
              ? '<img src="' + content + '">'
              : '<iframe width="100%" height="100%" src="' + content + '"></iframe>'
            let popup = window.open();
            popup.document.write(file);
          } else {
            showGlobalErrorMessage(E.FILE_NOT_FOUND)
          }
        })
        .catch((e) => {
          console.error(e);
        })
    }
  };

  const hasMetadata = [
    T.PHOTOS_VISIT_DATE,
    T.PROPERTY_DRAWING,
    T.PERMIT_BLUEPRINT,
    T.PROPERTY_LEGAL_DEFINITION,
    T.COMMON_HOUSE_PLAN_LOCATING_APARTMENT,
  ].includes(fileTypeID);

  return (
    <div className={styles.interaction}>
      <AbstractDropdownView
        subPath={`actions-${fileID}`}
        closeOnOutsideClick={clickOutsideAllowed}
        triggerContent={({toggleDropdown, triggerProps}) => (
          <img
            src={interaction}
            alt="interaction"
            onClick={toggleDropdown}
            {...triggerProps}
          />
        )}
        dropdownContent={({closeDropdown, itemProps}) => (
          <div className={styles.popup}>
            <ItemEditFile
              mimeType={mimeType}
              fileTypeID={fileTypeID}
              orderFileTypeID={orderFileTypeID}
              fileID={fileID}
              editFormValues={editFormValues}
              content={content}
              fileName={fileName}
              uploadFile={uploadFile}
              uploadOrderFile={uploadOrderFile}
              updateFile={updateFile}
              getFileList={getFileList}
              setClickOutsideAllowed={setClickOutsideAllowed}
              closeDropdown={closeDropdown}
              itemProps={itemProps}
              shumaId={shumaId}
              orderID={orderID}
              isFileFromOrder={isFileFromOrder}
            />
            {hasMetadata && !isFileFromOrder && (
              <ItemEditMetadata
                mimeType={mimeType}
                fileTypeID={fileTypeID}
                fileID={fileID}
                content={content}
                fileName={fileName}
                uploadFile={uploadFile}
                updateFile={updateFile}
                updateFileMetadata={updateFileMetadata}
                getFileList={getFileList}
                editFormValues={editFormValues}
                setClickOutsideAllowed={setClickOutsideAllowed}
                closeDropdown={closeDropdown}
                itemProps={itemProps}
                shumaId={shumaId}
              />
            )}
            {getType() && (
              <InteractionsMenuItem
                label={'פתיחה'}
                icon={viewIcon}
                onClick={() => handleFileOpenInTab()}
                {...itemProps[2]}
              />
            )}
            {!isFileFromOrder && (
              <InteractionsMenuItem
                label={'מעבר למסך המשויך'}
                icon={readMore}
                onClick={() => {
                  const path = P.GET_FORM_BY_ID_AND_STEP(shumaId, `${stepID}`);
                  history.push(path);
                }}
                {...itemProps[3]}
              />
            )}
            <InteractionsMenuItem
              label={'הורדת קובץ'}
              icon={downloadFileIcon}
              onClick={() => handleFileDownload()}
              {...itemProps[4]}
            />
            <InteractionsMenuItem
              label={'מחיקה'}
              icon={deleteFileIcon}
              onClick={() => setOpenConfirmation(true)}
              customClassnames={styles.interactionsMenuItemBottom}
              {...itemProps[5]}
            />
          </div>
        )}
      />
      <ConfirmDelete
        open={openConfirmation}
        closeModal={() => setOpenConfirmation(false)}
        fileID={fileID}
        fileName={fileName}
        onConfirm={async () => await handleDeleteFile()}
      />
      <AnimatedUploadingFilePopUp show={loading}/>
    </div>
  );
};

InteractionsMenu.propTypes = {
  fileTypeID: PropTypes.number,
  mimeType: PropTypes.string,
  fileID: PropTypes.number,
  stepID: PropTypes.number,
  content: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  fileName: PropTypes.string,
  deleteFile: PropTypes.func,
  uploadFile: PropTypes.func,
  updateFile: PropTypes.func,
  onFileDelete: PropTypes.func,
  updateFileMetadata: PropTypes.func,
  editFormValues: PropTypes.object,
  getFileList: PropTypes.func,
  showGlobalErrorMessage: PropTypes.func,
};

export default InteractionsMenu;
